import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Switch,
  CircularProgress,
} from "@mui/material";
import { Edit, Save, Cancel } from "@mui/icons-material";
import { HexColorPicker, HexColorInput } from "react-colorful"; // Import HexColorPicker and HexColorInput
import { axiosInstance } from "../../utility/apiInstance";
import toast from "react-hot-toast";
import { useRole } from "../MangeContextProvide";
import { Contact, LayoutPanelTop } from 'lucide-react'
import SearchInput from "../../common/SearchInput";


interface Flag {
  customer: any;
  added_date?: string;
  name: any;
  id: number;
  sNo: number;
  hex_color_code: string;
  description: string;
  status: "Active" | "Deactivated";
}

// Keeps the table area a stable height across loading, populated, and
// empty states so the page doesn't jump/collapse as data changes.
const MIN_TABLE_HEIGHT = 380;

function ManageFlags() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedFlag, setEditedFlag] = useState<Flag | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [communicationFlagCount, setCommunicationFlagCount] = useState(0)
  const [oceanCommunicationFlagCount, OceanSetCommunicationFlagCount] = useState(0)
  const [contactFlagCount, setContactFlagCount] = useState(0)


  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { globleCustomerId } = useRole() as any;

  const handleEdit = (flag: Flag) => {
    setEditingId(flag.id);
    setEditedFlag({
      ...flag,
      // keep the original format (DD/MM/YYYY)
      added_date: flag.added_date,
    });
  };

  const handleSave = async () => {
    try {
      setShowValidation(true);
      if (!editedFlag) return;
      if (!editedFlag?.name?.trim()) {
        return;
      }

      if (isAddingNew) {
        // Add new flag
        const payload = {
          name: editedFlag.name,
          description: editedFlag.description,
          hex_color_code: editedFlag.hex_color_code,
          is_active: editedFlag.status === "Active",
        };

        const { data } = await axiosInstance.post("/api/v1/flags", payload);

        getFlags();
        setShowValidation(false);

        toast.success(data.message || "Flag updated successfully");
        // const savedFlag = {
        //   ...data.data,
        //   sNo: editedFlag.sNo,
        //   status: data.data.is_active ? "Active" : "Deactive", 
        // };
        // setFlags(flags.map((f) => (f.id === editedFlag.id ? savedFlag : f)));

      } else {
        // Update existing flag
        const payload = {
          id: editedFlag.id,
          name: editedFlag.name,
          description: editedFlag.description,
          hex_color_code: editedFlag.hex_color_code,
          is_active: editedFlag.status === "Active",
        };

        const { data } = await axiosInstance.put("/api/v1/flags", payload);
        getFlags();
        toast.success(data.message || "Flag updated successfully");
        // setFlags(
        //   flags.map((flag) =>
        //     flag.id === editedFlag.id ? { ...data.data } : flag
        //   )
        // );
      }

      setEditedFlag(null);
      setIsAddingNew(false);
      setEditingId(null);

    } catch (error: any) {

      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }

    }
  };

  const handleCancel = () => {
    if (isAddingNew && editedFlag) {
      setShowValidation(false);

      // Remove the new flag from the list
      setFlags(flags.filter((flag) => flag.id !== editedFlag.id));
      setIsAddingNew(false);
      setOpenConfirm(false);

    }
    setEditingId(null);
    setEditedFlag(null);
    setOpenConfirm(false);

  };

  const handleAddFlag = () => {
    // Prevent adding new flag if already adding one or editing existing
    if (isAddingNew || editingId !== null) {
      return;
    }

    const newFlag: Flag = {
      id: Date.now(), // temporary ID
      sNo: flags.length + 1,
      name: "",
      hex_color_code: "#000000",
      description: "",
      status: "Active",
      customer: globleCustomerId,
    };

    setFlags([newFlag, ...flags]);
    setEditingId(newFlag.id);
    setEditedFlag(newFlag);
    setIsAddingNew(true);
    setPage(0);
  };

  async function getFlags() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/api/v1/flags");

      setFlags(data.data);
    } catch (error) {
      setFlags([])
    } finally {
      setLoading(false);
    }
  }
  async function getCountofAttachedFlags(id: any) {
    try {
      const { data } = await axiosInstance.get(`/api/v1/flags/all-attached-flag/${id}`);
      setCommunicationFlagCount(data?.data?.communicationFlags || 0)
      setContactFlagCount(data?.data?.contactsFlag || 0);
      OceanSetCommunicationFlagCount(data?.data?.oceanCommunicationFlags || 0)
    } catch (error) {

    }
  }


  useEffect(() => {
    getFlags();
  }, []);

  const handleInputChange = (field: keyof Flag, value: string) => {
    if (field === "name") {
      value = value.replace(/[^a-zA-Z0-9 _ -]/g, "");
    }

    if (editedFlag) {
      setEditedFlag({
        ...editedFlag,
        [field]: value,
      });
    }
  };

  // const handleStatusChange = (event: SelectChangeEvent) => {
  //   if (editedFlag) {
  //     setEditedFlag({
  //       ...editedFlag,
  //       status: event.target.value as "Active" | "Deactivated",
  //     });
  //   }
  // };


  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      // Trying to deactivate → show confirmation
      setOpenConfirm(true);
    } else {
      // Activate directly
      if (editedFlag) {
        setEditedFlag({ ...editedFlag, status: "Active" });
      }
      setFlags((prev) =>
        prev.map((f) =>
          f.id === editingId ? { ...f, status: "Active" } : f
        )
      );
    }
  };

  const updateFlagStatusApi = async (id: number, status: boolean) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/flags/${id}`,
        { status }
      );

      toast.success(status ? "Activated" : "Deactivated");
      return response.data;
    } catch (error: any) {
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }

      // 🔴 VERY IMPORTANT
      throw error;
    }
  };

  const handleStatusChangess = async (
    event: React.ChangeEvent<HTMLInputElement>,
    flag: any
  ) => {
    const isChecked = event.target.checked;

    // DEACTIVATE → ask confirmation
    if (!isChecked) {
      setEditingId(flag.id);
      setOpenConfirm(true);
      return;
    }

    // ACTIVATE → call API directly
    try {
      await updateFlagStatusApi(flag.id, true);

      setFlags((prev) =>
        prev.map((f) =>
          f.id === flag.id ? { ...f, status: "Active" } : f
        )
      );
    } catch (error) {
      // toast.error("Failed to activate flag");
    }
  };


  // Pagination handlers

  const confirmDeactivate = async () => {
    if (!editingId) return;

    try {
      // false = Inactive
      await updateFlagStatusApi(editingId, false);

      setFlags((prev) =>
        prev.map((f) =>
          f.id === editingId ? { ...f, status: "Deactivated" } : f
        )
      );

      setEditedFlag((prev) =>
        prev && prev.id === editingId
          ? { ...prev, status: "Deactivated" }
          : prev
      );
    } catch (error) {
      // toast.error("Failed to deactivate flag");
    } finally {
      setOpenConfirm(false);
      setEditingId(null);
    }
  };


  const handleChangePage = (
    event: any,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: any
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredFlags = flags.filter((flag) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      flag.name?.toLowerCase().includes(search) ||
      flag.description?.toLowerCase().includes(search) ||
      flag.hex_color_code?.toLowerCase().includes(search);

    const matchesStatus =
      statusFilter === "All" ? true : flag.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const paginatedFlags = filteredFlags.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const StatusBadge = ({ status }: { status: string }) => (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${status === "Active"
        ? "bg-green-100 text-green-800"
        : "bg-red-100 text-red-800"
        }`}
    >
      {status}
    </span>
  );

  const handleConfirm = () => {
    if (pendingStatus) {
      setEditedFlag((prev) =>
        prev ? { ...prev, status: pendingStatus as "Active" | "Deactivated" } : prev
      );
    }
    setOpenConfirm(false);
    setPendingStatus(null);
  };

  const cancelDeactivate = () => {
    setOpenConfirm(false);
  }


  return (
    <>
      <div className="old_dashboard_title">
        <h1 className="text-2xl font-bold text-gray-700 mt-[-15px] mb-[10px]">
          Manage Flags
        </h1>
      </div>

      <div className="whiteBox manageFlagsMain">
        {/* Controls Section */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleAddFlag}
            disabled={isAddingNew || editingId !== null}
            // className={`px-4 py-2 rounded font-semibold transition-colors ${isAddingNew || editingId !== null
            //   ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            //   : "bg-yellow-400 hover:bg-yellow-500 text-white"
            //   }`}

              className="btnTheme btnPadding"
          >
            Add Flag
          </button>
          <div className="flex-1 max-w-md">
            <SearchInput
              placeholder="Search Flags"
              onChange={setSearchTerm}
              width="100%"
            />
          </div>
          <FormControl size="small" className="min-w-[150px] manageFlagsSelect">
            <Select
              
              sx={{
                height: "auto",
                "& .MuiSelect-select": {
                  fontSize: "14px",
                },
              }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Deactivated">Deactivated</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Table Section */}
        <div className="mileTable mt-6">
        <TableContainer
          component={Paper}
          className="shadow-sm mileTableContainer"
          sx={{
            minHeight: MIN_TABLE_HEIGHT,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1 }}>
          <Table className="tableMilestone">
            <TableHead>
              <TableRow className="bg-gray-50">
                <TableCell className="font-semibold text-gray-700">
                  S.No
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Flag Name
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Hex Color
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Description
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Added on
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Status
                </TableCell>
                <TableCell className="font-semibold text-gray-700">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ height: MIN_TABLE_HEIGHT, border: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        gap: 1.5,
                      }}
                    >
                      <CircularProgress size={32} thickness={4} />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Loading flags...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredFlags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ height: MIN_TABLE_HEIGHT, border: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No flags found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFlags.map((flag, index) => {
                const rowBorder = { borderBottom: "1px solid rgba(224, 224, 224, 1) !important" };
                return (
                <TableRow key={flag.id} className="hover:bg-gray-50">
                  <TableCell sx={rowBorder}>{index + 1}</TableCell>

                  <TableCell sx={rowBorder}>
                    {editingId === flag.id ? (
                      <TextField
                        value={editedFlag?.name || ""}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        size="small"
                        className="min-w-[120px]"
                        required
                        error={showValidation && !editedFlag?.name?.trim()}
                        helperText={
                          showValidation && !editedFlag?.name?.trim()
                            ? "Flag Name is required"
                            : ""
                        }

                      />
                    ) : (
                      flag.name
                    )}
                  </TableCell>

                  <TableCell sx={rowBorder}>
                    {editingId === flag.id ? (

                      <div
                        key={editedFlag?.id || "new-flag-color-picker"}
                        style={{
                          width: "60%",
                          padding: "8px",
                          display: "flex",
                          flexDirection: "column",
                          height: "160px",
                          gap: "8px",
                          paddingLeft: "15px",
                          paddingRight: "15px",
                        }}
                      >
                        <HexColorPicker
                          color={editedFlag?.hex_color_code || "#000000"}
                          onChange={(newColor) =>
                            handleInputChange("hex_color_code", newColor)
                          }
                          style={{ width: "100%" }}
                        />
                        <HexColorInput
                          color={editedFlag?.hex_color_code || "#000000"}
                          onChange={(newColor) =>
                            handleInputChange("hex_color_code", newColor)
                          }
                          prefixed
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                    ) : (
                      // End of the fix for ResizeObserver error
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: "90px",
                            height: "30px",
                            backgroundColor:
                              flag.hex_color_code || "transparent",
                            border: "1px solid #ccc",
                            marginRight: "8px",
                            borderRadius: "4px",
                          }}
                        />
                        <span
                          style={{
                            fontFamily: "monospace",
                            color: "white",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "90px",
                            height: "30px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {flag.hex_color_code}
                        </span>
                      </div>
                    )}
                  </TableCell>

                  <TableCell style={{ minWidth: "250px" }} sx={rowBorder}
                  >
                    {editingId === flag.id ? (
                      <TextField
                        value={editedFlag?.description || ""}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        size="small"
                        // multiline
                        className="min-w-[200px]"
                      />
                    ) : (
                      flag.description
                    )}
                  </TableCell>

                  <TableCell sx={rowBorder}>
                    {flag.added_date
                    }
                  </TableCell>

                  <TableCell sx={rowBorder}>

                    {editingId === flag.id ? (
                      <FormControl size="small" className="min-w-[120px]">
                        {/* <Select
                          value={editedFlag?.status || "Active"}
                          onChange={handleStatusChangesss}
                          variant="outlined"
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Deactivated">Deactivated</MenuItem>
                        </Select> */}

                        <Switch
                          className="thinSwitch"
                          checked={
                            editingId === flag.id
                              ? editedFlag?.status === "Active"
                              : flag.status === "Active"}
                          onChange={handleStatusChange}
                          color="primary"
                        />
                      </FormControl>
                    ) : (
                      <>
                        {/* <StatusBadge status={flag.status} /> */}
                        <Switch
                          className="thinSwitch"
                          checked={
                            editingId === flag.id
                              ? editedFlag?.status === "Active"
                              : flag.status === "Active"}
                          onChange={(e) => handleStatusChangess(e, flag)}
                          color="primary"
                        />
                      </>
                    )}
                  </TableCell>

                  <TableCell sx={rowBorder}>
                    <div className="flex items-center gap-1" onClick={() => getCountofAttachedFlags(flag.id)}>
                      {editingId === flag.id ? (
                        <>
                          <IconButton
                            onClick={handleSave}
                            size="small"
                            className="iconMilestone_save"
                          >
                            <Save fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={handleCancel}
                            size="small"
                            className="iconMilestone_close"
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        globleCustomerId &&
                        globleCustomerId === flag.customer && (
                          <IconButton
                            disabled={flag.status === "Deactivated"}
                            onClick={() => handleEdit(flag)}
                            size="small"
                            className="iconMilestone_edit"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
          </Box>

          {/* Pagination — pinned to the bottom of the container via flex layout above */}
          {filteredFlags.length > 0 && (
            <TablePagination
              component="div"
              count={filteredFlags.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20]}
              className="border-t"
            />
          )}
        </TableContainer>
        </div>
      </div>
      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Are you sure you want to deactivate this flag?
          </Typography>

          {/* Attached info */}
          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* Communication Templates */}
            {communicationFlagCount !== 0 && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LayoutPanelTop size={20} />
                <Typography variant="body1" color="text.primary">
                  Communication Templates attached:{" "}
                  <Box component="span" fontWeight="bold" color="primary.main">
                    {communicationFlagCount}
                  </Box>
                </Typography>
              </Stack>
            )}

            {/* Ocean Communication Templates */}
            {oceanCommunicationFlagCount !== 0 && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LayoutPanelTop size={20} />
                <Typography variant="body1" color="text.primary">
                  Ocean Communication Templates attached:{" "}
                  <Box component="span" fontWeight="bold" color="primary.main">
                    {oceanCommunicationFlagCount}
                  </Box>
                </Typography>
              </Stack>
            )}

            {/* Contacts */}
            {contactFlagCount !== 0 && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Contact size={20} />
                <Typography variant="body1" color="text.primary">
                  Contacts attached:{" "}
                  <Box component="span" fontWeight="bold" color="primary.main">
                    {contactFlagCount}
                  </Box>
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelDeactivate}>No</Button>
          <Button onClick={confirmDeactivate} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      {/* <Dialog open={openConfirm} onClose={cancelDeactivate}>
        <DialogTitle>Deactivate Status?</DialogTitle>
        <DialogContent>
          Are you sure you want to deactivate this?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeactivate}>Cancel</Button>
          <Button
            onClick={confirmDeactivate}
            color="error"
            variant="contained"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog> */}
    </>
  );
}

export default ManageFlags;